import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import {
  categoryRequiresClassSelection,
  timetableOptionsForCategory,
  ALL_LOCATIONS,
  ageGroupsForCategory,
} from "@/data/bookingOptions";
import { DAY_ORDER } from "@/data/timetable";

// Cinematic booking-details modal opened BEFORE Stripe redirect / PayPal flow.
// Captures everything we need so the confirmation page (and admin email)
// know exactly who booked what.
//
// Props:
//   open: boolean
//   onClose: () => void
//   product: { sku, title, price, category, interval? }
//   onConfirm: async (bookingDetails) => Promise<void>
//     — called when the user submits. The parent runs the actual checkout
//       (Stripe redirect or PayPal createOrder). Keep returning a promise so
//       we can show a loader while it resolves.
//   submitLabel: button label ("Continue to PayPal" / "Continue to Card")
export const BookingDetailsModal = ({ open, onClose, product, onConfirm, submitLabel = "Continue" }) => {
  const requiresClassPick = !!product && categoryRequiresClassSelection(product.category);
  const ageGroups = useMemo(() => ageGroupsForCategory(product?.category), [product?.category]);
  const timetableOpts = useMemo(() => timetableOptionsForCategory(product?.category), [product?.category]);

  const isKids = product?.category === "kids";
  // The Kids Taster requires more child-safety detail (SEND, emergency
  // contact, photo consent, how-they-heard-about-us) BEFORE payment.
  const isKidsTaster = product?.sku === "kids-taster-5";

  const [form, setForm] = useState({
    student_name: "",
    guardian_name: "",
    email: "",
    phone: "",
    age_group: "",
    class_type: "",
    location: "",
    preferred_day: "",
    preferred_time: "",
    notes: "",
    whatsapp_optin: false,
    // Kids-Taster only — sent as part of booking payload (server ignores when unused)
    child_age: "",
    send_needs: "",
    emergency_contact: "",
    photo_video_consent: false,
    referral_source: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset on open/close for a clean slate per checkout
  useEffect(() => {
    if (open) {
      setForm({
        student_name: "",
        guardian_name: "",
        email: "",
        phone: "",
        age_group: "",
        class_type: "",
        location: "",
        preferred_day: "",
        preferred_time: "",
        notes: "",
        whatsapp_optin: false,
        child_age: "",
        send_needs: "",
        emergency_contact: "",
        photo_video_consent: false,
        referral_source: "",
      });
      setError("");
      setSubmitting(false);
      const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", onKey);
      return () => {
        document.body.style.overflow = prevOverflow;
        document.removeEventListener("keydown", onKey);
      };
    }
  }, [open, onClose]);

  // When user picks a class slot from the dropdown, auto-fill day/time/location
  const onPickSlot = (idx) => {
    const slot = timetableOpts[Number(idx)];
    if (!slot) {
      setForm((f) => ({ ...f, class_type: "", preferred_day: "", preferred_time: "", location: "" }));
      return;
    }
    setForm((f) => ({
      ...f,
      class_type: slot.title,
      preferred_day: slot.day,
      preferred_time: slot.time,
      location: slot.location,
    }));
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.student_name || !form.email || !form.phone) {
      setError("Please fill in name, email and phone — they're required.");
      return;
    }
    if (isKids && !form.guardian_name) {
      setError("For Kids bookings we also need a parent / guardian name.");
      return;
    }
    if (isKidsTaster) {
      if (!form.child_age) { setError("Please tell us the child's age."); return; }
      if (!form.emergency_contact) { setError("Please provide an emergency contact (name + phone)."); return; }
    }
    if (requiresClassPick && (!form.class_type || !form.location)) {
      setError("Please select which class and location you're booking for.");
      return;
    }
    if (requiresClassPick && (!form.preferred_day || !form.preferred_time)) {
      setError("Please pick the day and time slot for your class.");
      return;
    }
    try {
      setSubmitting(true);
      await onConfirm(form);
    } catch (err) {
      setError(err?.message || "Couldn't continue — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || typeof document === "undefined" || !product) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      data-testid="booking-details-modal"
      className="fixed inset-0 z-[210] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-brand-yellow/30 bg-gradient-to-b from-brand-purpleDeep/40 via-zinc-950 to-black shadow-[0_30px_120px_rgba(0,0,0,0.75)]"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 px-6 py-4 border-b border-white/10 bg-black/50 backdrop-blur">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/70 to-transparent pointer-events-none" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow">Booking details · Step 1 of 2</div>
            <div data-testid="booking-details-title" className="font-display text-base sm:text-lg text-white tracking-wide truncate">
              {product.title} <span className="text-zinc-500">· £{Number(product.price).toFixed(product.price % 1 ? 2 : 0)}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="booking-details-close"
            aria-label="Close"
            className="w-9 h-9 rounded-full bg-white/5 border border-white/15 flex items-center justify-center text-zinc-300 hover:text-white hover:bg-white/15 transition-colors flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 sm:p-7 space-y-5">
          {/* Student + guardian */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Student name *" testId="bd-student">
              <input
                type="text" required value={form.student_name} onChange={update("student_name")}
                data-testid="bd-student-input"
                className="usdd-input"
                placeholder="e.g. Maya Johnson"
              />
            </Field>
            <Field label={isKids ? "Parent / Guardian name *" : "Parent / Guardian (if under 18)"} testId="bd-guardian">
              <input
                type="text" required={isKids} value={form.guardian_name} onChange={update("guardian_name")}
                data-testid="bd-guardian-input"
                className="usdd-input"
                placeholder="e.g. Sam Johnson"
              />
            </Field>
          </div>

          {/* Email + phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Email *" testId="bd-email">
              <input
                type="email" required value={form.email} onChange={update("email")}
                data-testid="bd-email-input"
                className="usdd-input"
                placeholder="you@example.com"
              />
            </Field>
            <Field label="Phone / WhatsApp *" testId="bd-phone">
              <input
                type="tel" required value={form.phone} onChange={update("phone")}
                data-testid="bd-phone-input"
                className="usdd-input"
                placeholder="07XXX XXXXXX"
              />
            </Field>
          </div>

          {/* Age group */}
          <Field label="Age group" testId="bd-age-group">
            <div className="flex flex-wrap gap-2">
              {ageGroups.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, age_group: a }))}
                  data-testid={`bd-age-${a}`}
                  className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.18em] transition-all ${
                    form.age_group === a
                      ? "bg-brand-yellow text-black"
                      : "border border-white/15 bg-white/5 text-zinc-300 hover:border-brand-yellow/50"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </Field>

          {/* Class selection */}
          {requiresClassPick && (
            <Field label={`Which class are you booking for? *`} testId="bd-class-pick">
              <select
                onChange={(e) => onPickSlot(e.target.value)}
                data-testid="bd-class-pick-select"
                className="usdd-input cursor-pointer"
                value={timetableOpts.findIndex((s) => s.title === form.class_type && s.day === form.preferred_day && s.time === form.preferred_time)}
              >
                <option value={-1}>— Select a class from the timetable —</option>
                {timetableOpts.map((s, i) => (
                  <option key={`${s.day}-${s.time}-${s.title}-${i}`} value={i}>
                    {s.day} · {s.time} · {s.title} · {s.location}
                  </option>
                ))}
              </select>
              <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                Don't see the right slot? Pick &quot;Other&quot; below and tell us in the notes.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                <input
                  type="text" placeholder="Other class…"
                  value={form.class_type} onChange={update("class_type")}
                  data-testid="bd-class-text"
                  className="usdd-input"
                />
                <select
                  value={form.location} onChange={update("location")}
                  data-testid="bd-location-select"
                  className="usdd-input"
                >
                  <option value="">Location *</option>
                  {ALL_LOCATIONS.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
                <select
                  value={form.preferred_day} onChange={update("preferred_day")}
                  data-testid="bd-day-select"
                  className="usdd-input"
                >
                  <option value="">Day *</option>
                  {DAY_ORDER.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <input
                type="text" placeholder="Time * (e.g. 19:00 – 20:30)"
                value={form.preferred_time} onChange={update("preferred_time")}
                data-testid="bd-time-text"
                className="usdd-input mt-3"
              />
            </Field>
          )}

          {/* Notes */}
          <Field label="Notes / access needs" testId="bd-notes">
            <textarea
              rows={3} value={form.notes} onChange={update("notes")}
              data-testid="bd-notes-input"
              className="usdd-input resize-none"
              placeholder="Anything we should know — e.g. previous experience, access requirements, allergies."
            />
          </Field>

          {/* WhatsApp opt-in */}
          <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="bd-whatsapp-optin">
            <input
              type="checkbox"
              checked={form.whatsapp_optin}
              onChange={(e) => setForm({ ...form, whatsapp_optin: e.target.checked })}
              data-testid="bd-whatsapp-optin-input"
              className="mt-1 h-4 w-4 rounded border-white/30 bg-white/10 text-brand-yellow focus:ring-brand-yellow"
            />
            <span className="text-xs text-zinc-300 leading-relaxed">
              <span className="font-bold text-white">Send me class updates on WhatsApp.</span>
              <span className="block text-zinc-500 mt-0.5">
                Schedule changes, last-minute cancellations and reminders. Unsubscribe any time by replying STOP.
              </span>
            </span>
          </label>

          {/* Kids Taster — extra safeguarding fields shown only when relevant */}
          {isKidsTaster && (
            <div className="space-y-4 pt-4 border-t border-brand-yellow/20" data-testid="bd-kids-taster-section">
              <div className="text-[10px] uppercase tracking-[0.28em] text-brand-yellow font-bold">Kids Taster · Safeguarding</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Child age *" testId="bd-child-age">
                  <input
                    type="text" required value={form.child_age} onChange={update("child_age")}
                    data-testid="bd-child-age-input"
                    className="usdd-input"
                    placeholder="e.g. 9"
                  />
                </Field>
                <Field label="Emergency contact (name + phone) *" testId="bd-emergency-contact">
                  <input
                    type="text" required value={form.emergency_contact} onChange={update("emergency_contact")}
                    data-testid="bd-emergency-contact-input"
                    className="usdd-input"
                    placeholder="e.g. Sam Johnson · 07700 900123"
                  />
                </Field>
              </div>
              <Field label="SEND / access needs" testId="bd-send-needs">
                <textarea
                  rows={2} value={form.send_needs} onChange={update("send_needs")}
                  data-testid="bd-send-needs-input"
                  className="usdd-input resize-none"
                  placeholder="Anything we should know to support your child best (optional)."
                />
              </Field>
              <Field label="How did you hear about us?" testId="bd-referral-source">
                <input
                  type="text" value={form.referral_source} onChange={update("referral_source")}
                  data-testid="bd-referral-source-input"
                  className="usdd-input"
                  placeholder="Instagram · friend · school · USDD parent referral name…"
                />
              </Field>
              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="bd-photo-consent">
                <input
                  type="checkbox"
                  checked={form.photo_video_consent}
                  onChange={(e) => setForm({ ...form, photo_video_consent: e.target.checked })}
                  data-testid="bd-photo-consent-input"
                  className="mt-1 h-4 w-4 rounded border-white/30 bg-white/10 text-brand-yellow focus:ring-brand-yellow"
                />
                <span className="text-xs text-zinc-300 leading-relaxed">
                  <span className="font-bold text-white">I consent to photo / video being captured during this taster class.</span>
                  <span className="block text-zinc-500 mt-0.5">
                    Optional. Used internally and (only with your further consent) for USDD social media. See our Photography &amp; Video Consent Policy.
                  </span>
                </span>
              </label>
            </div>
          )}

          {/* Error */}
          {error && (
            <div data-testid="booking-details-error" className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-white/10 bg-black/70 backdrop-blur">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5">
            <ShieldCheck size={12} /> Your details stay private
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="booking-details-submit"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-[0.2em] hover:bg-brand-yellowHover hover:scale-[1.02] transition-all glow-yellow disabled:opacity-60 w-full sm:w-auto"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
            {submitLabel}
            <ArrowRight size={14} />
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

const Field = ({ label, children, testId }) => (
  <label data-testid={testId} className="block">
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 font-bold">{label}</div>
    {children}
  </label>
);
